import { useState, useEffect, useCallback } from 'react';
import useAutoRefresh from '../hooks/useAutoRefresh';
import useWindowWidth from '../hooks/useWindowWidth';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { ArrowLeft, Trash2 } from 'lucide-react';

const API = process.env.REACT_APP_API_URL;
const MONTHS = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

function calcHours(von, bis, pauseMin) {
    if (!von || !bis) return 0;
    const [vh, vm] = von.split(':').map(Number);
    const [bh, bm] = bis.split(':').map(Number);
    const totalMin = (bh * 60 + bm) - (vh * 60 + vm) - (Number(pauseMin) || 0);
    return Math.max(0, totalMin / 60);
}

function Rechner() {
    const [a, setA] = useState('');
    const [b, setB] = useState('');
    const [op, setOp] = useState('+');
    const [result, setResult] = useState(null);

    const calc = () => {
        const x = parseFloat(a);
        const y = parseFloat(b);
        if (isNaN(x) || isNaN(y)) return;
        const r = op === '+' ? x + y : op === '-' ? x - y : op === '*' ? x * y : y !== 0 ? x / y : null;
        setResult(r !== null ? parseFloat(r.toFixed(4)) : 'Fehler');
    };

    const inputS = {
        background: 'var(--bg-primary)',
        border: '1px solid var(--border)',
        borderRadius: '8px',
        color: 'var(--text-primary)',
        fontFamily: 'inherit',
        fontSize: '14px',
        padding: '8px 12px',
        width: '100%',
    };

    return (
        <div style={{
            background: 'var(--card-bg)',
            border: '1px solid var(--border)',
            borderRadius: '12px',
            padding: '20px',
            position: 'sticky',
            top: '100px',
        }}>
            <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: '16px', color: 'var(--text-primary)' }}>Rechner</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <input style={inputS} placeholder="Zahl 1" value={a} onChange={e => setA(e.target.value)} type="text" inputMode="decimal" />
                <select value={op} onChange={e => setOp(e.target.value)} style={{ ...inputS, cursor: 'pointer' }}>
                    <option value="+">+</option>
                    <option value="-">−</option>
                    <option value="*">×</option>
                    <option value="/">÷</option>
                </select>
                <input style={inputS} placeholder="Zahl 2" value={b} onChange={e => setB(e.target.value)} type="text" inputMode="decimal" />
                <button onClick={calc} style={{
                    background: 'var(--accent)', color: '#000', border: 'none',
                    borderRadius: '8px', padding: '10px', fontWeight: 700, cursor: 'pointer', fontSize: '14px'
                }}>=</button>
                {result !== null && (
                    <div style={{
                        background: 'var(--bg-secondary)', borderRadius: '8px', padding: '10px',
                        textAlign: 'center', fontWeight: 800, fontSize: '20px', color: 'var(--accent)'
                    }}>{result}</div>
                )}
            </div>
        </div>
    );
}

const SHARED_USERS = ['lino', 'tobi'];

export default function ArbeitsZeit() {
    const { user } = useAuth();
    const token = user?.token;
    const isSharedUser = SHARED_USERS.includes(user?.username?.toLowerCase());
    const isMobile = useWindowWidth() < 768;

    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    const showPrevYear = currentMonth === 0;

    const [year] = useState(currentYear);
    const [month, setMonth] = useState(now.getMonth() + 1);
    const [day, setDay] = useState(String(now.getDate()));
    const [von, setVon] = useState('');
    const [bis, setBis] = useState('');
    const [pause, setPause] = useState('');
    const [persons, setPersons] = useState({ Lino: false, Tobi: false });
    const [note, setNote] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [entries, setEntries] = useState([]);
    const [selectedYear, setSelectedYear] = useState(currentYear);

    const fetchEntries = useCallback(async () => {
        const res = await fetch(`${API}/api/work-hours`, {
            headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) setEntries(await res.json());
    }, [token]);

    useEffect(() => { fetchEntries(); }, [fetchEntries]);
    useAutoRefresh(fetchEntries, 10000);

    const handleDayInput = (e) => {
        const val = e.target.value.replace(/\D/g, '').slice(0, 2);
        setDay(val);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const dayNum = parseInt(day);
        if (!day || dayNum < 1 || dayNum > 31) { setError('Gültigen Tag eingeben (1-31)'); return; }
        const selected = isSharedUser ? Object.keys(persons).filter(p => persons[p]) : [user.username];
        if (isSharedUser && selected.length === 0) { setError('Mindestens eine Person auswählen'); return; }
        if (!von || !bis) { setError('Von und Bis eintragen'); return; }
        const hours = calcHours(von, bis, pause);
        if (hours <= 0) { setError('Bis muss nach Von liegen'); return; }
        setError('');
        setLoading(true);

        const dateStr = `${selectedYear}-${String(month).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
        const noteStr = `${von}–${bis}${pause ? ` (${pause} Min. Pause)` : ''}${note ? ' | ' + note : ''}`;

        for (const person of selected) {
            await fetch(`${API}/api/work-hours`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ date: dateStr, person, hours: parseFloat(hours.toFixed(2)), note: noteStr })
            });
        }
        setVon(''); setBis(''); setPause(''); setNote('');
        if (isSharedUser) setPersons({ Lino: false, Tobi: false });
        setLoading(false);
        fetchEntries();
    };

    const handleDelete = async (id) => {
        await fetch(`${API}/api/work-hours/${id}`, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token}` }
        });
        fetchEntries();
    };

    const currentMonthStr = `${selectedYear}-${String(month).padStart(2, '0')}`;
    const monthEntries = entries.filter(e => e.date.startsWith(currentMonthStr));
    const monthTotals = monthEntries.reduce((acc, e) => {
        acc[e.person] = (acc[e.person] || 0) + e.hours;
        return acc;
    }, {});
    const allTotals = entries.reduce((acc, e) => {
        acc[e.person] = (acc[e.person] || 0) + e.hours;
        return acc;
    }, {});

    const previewHours = calcHours(von, bis, pause);

    const inputStyle = {
        background: 'var(--bg-secondary)',
        border: '2px solid var(--border)',
        borderRadius: '10px',
        color: 'var(--text-primary)',
        fontFamily: 'inherit',
        fontSize: '16px',
        padding: '14px 18px',
        width: '100%',
    };

    const selectStyle = { ...inputStyle, cursor: 'pointer', appearance: 'none', WebkitAppearance: 'none' };

    return (
        <div className="section" style={{ minHeight: 'calc(100vh - 140px)' }}>
            <div className="container">
                <Link to="/privat" className="icon-button" style={{ display: 'inline-flex', gap: '8px', alignItems: 'center', marginBottom: '32px' }}>
                    <ArrowLeft size={20} />
                    <span style={{ fontWeight: 500 }}>Zurück</span>
                </Link>

                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                    <h1 className="section-title">Arbeitszeitkalender</h1>
                    <p className="section-subtitle" style={{ marginBottom: '48px' }}>Arbeitszeiten eintragen und auswerten</p>

                    {/* Zweispalten-Layout: Formular + Rechner */}
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 200px', gap: '24px', maxWidth: '860px', margin: '0 auto 48px', alignItems: 'start' }}>

                        {/* Formular */}
                        <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '12px', padding: '32px' }}>
                            <form onSubmit={handleSubmit}>

                                {/* Datum */}
                                <div className="form-group">
                                    <label className="form-label">Datum</label>
                                    <div style={{ display: 'flex', gap: '10px' }}>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            maxLength="2"
                                            value={day}
                                            onChange={handleDayInput}
                                            placeholder="Tag"
                                            style={{ ...inputStyle, flex: '0 0 70px', padding: '14px 10px', textAlign: 'center' }}
                                        />
                                        <select value={month} onChange={e => setMonth(Number(e.target.value))} style={{ ...selectStyle, flex: 1 }}>
                                            {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                                        </select>
                                        {showPrevYear ? (
                                            <select value={selectedYear} onChange={e => setSelectedYear(Number(e.target.value))} style={{ ...selectStyle, flex: '0 0 100px' }}>
                                                <option value={currentYear - 1}>{currentYear - 1}</option>
                                                <option value={currentYear}>{currentYear}</option>
                                            </select>
                                        ) : (
                                            <div style={{ ...inputStyle, flex: '0 0 90px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)', background: 'var(--bg-secondary)' }}>
                                                {currentYear}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Von / Bis */}
                                <div className="form-group">
                                    <label className="form-label">Arbeitszeit</label>
                                    <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px' }}>Von</div>
                                            <input type="text" value={von} onChange={e => setVon(e.target.value)} placeholder="08:00" style={inputStyle} />
                                        </div>
                                        <div style={{ paddingBottom: '16px', color: 'var(--text-secondary)' }}>–</div>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px' }}>Bis</div>
                                            <input type="text" value={bis} onChange={e => setBis(e.target.value)} placeholder="16:00" style={inputStyle} />
                                        </div>
                                    </div>
                                </div>

                                {/* Pause */}
                                <div className="form-group">
                                    <label className="form-label">Pause <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>(Minuten)</span></label>
                                    <input type="number" min="0" step="5" value={pause} onChange={e => setPause(e.target.value)} placeholder="0" style={inputStyle} />
                                </div>

                                {/* Vorschau */}
                                {von && bis && (
                                    <div style={{
                                        padding: '12px 16px',
                                        background: previewHours > 0 ? 'rgba(251,146,60,0.08)' : 'rgba(255,80,80,0.08)',
                                        border: `1px solid ${previewHours > 0 ? 'var(--accent)' : '#ff5050'}`,
                                        borderRadius: '8px', marginBottom: '20px',
                                        fontSize: '14px', color: previewHours > 0 ? 'var(--accent)' : '#ff5050', fontWeight: 600
                                    }}>
                                        {previewHours > 0 ? `Arbeitszeit: ${previewHours.toFixed(2)} Stunden` : 'Ungültige Zeitangabe'}
                                    </div>
                                )}

                                {/* Person – nur für Lino/Tobi */}
                                {isSharedUser && (
                                    <div className="form-group">
                                        <label className="form-label">Person</label>
                                        <div style={{ display: 'flex', gap: '16px' }}>
                                            {['Lino', 'Tobi'].map(person => (
                                                <label key={person} style={{
                                                    display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer',
                                                    padding: '12px 20px',
                                                    background: persons[person] ? 'rgba(251,146,60,0.1)' : 'var(--bg-secondary)',
                                                    border: `2px solid ${persons[person] ? 'var(--accent)' : 'var(--border)'}`,
                                                    borderRadius: '8px', transition: 'all 0.2s', flex: 1, justifyContent: 'center'
                                                }}>
                                                    <input type="checkbox" checked={persons[person]} onChange={e => setPersons(prev => ({ ...prev, [person]: e.target.checked }))} style={{ accentColor: 'var(--accent)', width: '18px', height: '18px' }} />
                                                    <span style={{ fontWeight: 600 }}>{person}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Notiz */}
                                <div className="form-group">
                                    <label className="form-label">Notiz <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>(Optional)</span></label>
                                    <input type="text" value={note} onChange={e => setNote(e.target.value)} placeholder="z.B. Kundenmeeting" style={inputStyle} />
                                </div>

                                {error && (
                                    <div style={{ color: '#ff5050', fontSize: '14px', marginBottom: '16px', padding: '10px', background: 'rgba(255,80,80,0.1)', borderRadius: '8px' }}>{error}</div>
                                )}

                                <button type="submit" className="btn-primary" disabled={loading}>
                                    {loading ? 'Wird gespeichert...' : 'Eintragen'}
                                </button>
                            </form>
                        </div>

                        {/* Rechner */}
                        <Rechner />
                    </div>

                    {/* Monatsauswahl + Summen */}
                    <div style={{ maxWidth: '860px', margin: '0 auto' }}>
                        <div style={{ display: 'flex', gap: '10px', marginBottom: '24px', flexWrap: 'wrap', alignItems: 'center' }}>
                            <select value={month} onChange={e => setMonth(Number(e.target.value))} style={{ ...selectStyle, width: 'auto', padding: '10px 16px' }}>
                                {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                            </select>
                            {showPrevYear && (
                                <select value={selectedYear} onChange={e => setSelectedYear(Number(e.target.value))} style={{ ...selectStyle, width: 'auto', padding: '10px 16px' }}>
                                    <option value={currentYear - 1}>{currentYear - 1}</option>
                                    <option value={currentYear}>{currentYear}</option>
                                </select>
                            )}
                        </div>

                        {Object.keys(monthTotals).length > 0 && (
                            <div style={{ display: 'flex', gap: '16px', marginBottom: '32px', flexWrap: 'wrap' }}>
                                {Object.entries(monthTotals).map(([person, total]) => (
                                    <div key={person} style={{ background: 'var(--card-bg)', border: '1px solid var(--accent)', borderRadius: '12px', padding: '20px 32px', minWidth: '160px' }}>
                                        <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '4px' }}>{person}</div>
                                        <div style={{ fontSize: '32px', fontWeight: 800, color: 'var(--accent)' }}>{total.toFixed(2)}h</div>
                                        <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '4px' }}>{MONTHS[month - 1]} {selectedYear}</div>
                                        <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '8px', borderTop: '1px solid var(--border)', paddingTop: '8px' }}>
                                            Gesamt: {(allTotals[person] || 0).toFixed(2)}h
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {monthEntries.length > 0 && (
                            <div>
                                <h2 style={{ fontSize: '20px', fontWeight: 700, marginBottom: '16px' }}>
                                    {MONTHS[month - 1]} {selectedYear}
                                </h2>
                                {isSharedUser ? (
                                    ['Lino', 'Tobi'].map(person => {
                                        const personEntries = monthEntries.filter(e => e.person === person);
                                        if (personEntries.length === 0) return null;
                                        return (
                                            <div key={person} style={{ marginBottom: '24px' }}>
                                                <h3 style={{ fontSize: '16px', fontWeight: 600, color: 'var(--accent)', marginBottom: '10px' }}>
                                                    {person} – {(monthTotals[person] || 0).toFixed(2)}h
                                                </h3>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                    {personEntries.map(entry => (
                                                        <div key={entry.id} style={{
                                                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                            padding: '12px 16px', background: 'var(--card-bg)',
                                                            border: '1px solid var(--border)', borderRadius: '8px',
                                                        }}>
                                                            <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
                                                                <span style={{ color: 'var(--text-secondary)', fontSize: '13px', minWidth: '90px' }}>{entry.date}</span>
                                                                <span style={{ fontWeight: 700, color: 'var(--accent)' }}>{entry.hours}h</span>
                                                                {entry.note && <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>{entry.note}</span>}
                                                            </div>
                                                            <button onClick={() => handleDelete(entry.id)} className="icon-button" style={{ color: '#ff5050', flexShrink: 0 }}>
                                                                <Trash2 size={15} />
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                        {monthEntries.map(entry => (
                                            <div key={entry.id} style={{
                                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                padding: '12px 16px', background: 'var(--card-bg)',
                                                border: '1px solid var(--border)', borderRadius: '8px',
                                            }}>
                                                <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
                                                    <span style={{ color: 'var(--text-secondary)', fontSize: '13px', minWidth: '90px' }}>{entry.date}</span>
                                                    <span style={{ fontWeight: 700, color: 'var(--accent)' }}>{entry.hours}h</span>
                                                    {entry.note && <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>{entry.note}</span>}
                                                </div>
                                                <button onClick={() => handleDelete(entry.id)} className="icon-button" style={{ color: '#ff5050', flexShrink: 0 }}>
                                                    <Trash2 size={15} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {monthEntries.length === 0 && (
                            <div style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: '40px 0', fontSize: '14px' }}>
                                Keine Einträge für {MONTHS[month - 1]} {selectedYear}
                            </div>
                        )}
                    </div>
                </motion.div>
            </div>
        </div>
    );
}